/*
  <amor-wishlist>: the Wishlist page (spec chapter 20, update 2 chapter 7).
  The saved ids come from window.DemoStore; the cards are clones of the
  hidden templates that main-wishlist.liquid renders for the whole catalog.
  A shared link carries the ids in the address (?items=1,2,3), so it works
  without a server, and shows that list read only.
*/
(() => {
  const UNDO_MS = 5000;
  const RECS_LIMIT = 6;

  class AmorWishlist extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.pool = new Map([...this.querySelectorAll('[data-wishlist-pool] template[data-product-id]')].map((t) => [t.dataset.productId, t]));
      // Noa's items in the color she saved them in, by "id:COLOR".
      this.colored = new Map([...this.querySelectorAll('[data-wishlist-pool] template[data-wishlist-colored]')].map((t) => [t.dataset.wishlistColored, t]));
      const shared = new URLSearchParams(window.location.search).get('items');
      this.sharedIds = shared ? shared.split(',').filter(Boolean) : null;

      this.addEventListener('click', (event) => this.onClick(event));
      // Hearts elsewhere on the page (quick add, recommendations) change the list too.
      document.addEventListener('demo-store:change', (event) => {
        if (event.detail?.key !== 'wishlist' || this.ownChange) return;
        if (!this.sharedIds) this.render();
      });
      this.render();
    }

    ids() {
      return this.sharedIds || window.DemoStore?.wishlist() || [];
    }

    countText(count) {
      return count === 1 ? this.dataset.countOne : this.dataset.countOther.replace('__COUNT__', count);
    }

    /** A card for the grid: the catalog card, in the color it was saved in, with an X in place of the heart (own list only). */
    card(id) {
      const template = this.pool.get(String(id));
      if (!template) return null;
      const color = this.sharedIds ? null : window.DemoStore?.wishlistColor?.(id)?.color;
      const source = (color && this.colored.get(`${id}:${color}`)) || template;
      const item = source.content.firstElementChild.cloneNode(true);
      item.dataset.productId = id;
      // A color chosen on another card (stage א7, note 18): card.js shows the card in it.
      if (color && source === template) {
        const card = item.querySelector('[data-card]');
        if (card) card.dataset.initialColor = color;
      }
      const plus = item.querySelector('[data-quick-add]');
      if (plus && template.hasAttribute('data-sold-out')) plus.setAttribute('data-quick-add-notify', '');
      if (!this.sharedIds) {
        const title = item.querySelector('.card__title')?.textContent.trim() || '';
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'icon-button icon-button--solid card__heart wishlist__remove';
        remove.dataset.wishlistRemove = id;
        remove.setAttribute('aria-label', this.dataset.removeLabel.replace('__TITLE__', title));
        remove.append(this.querySelector('template[data-remove-icon]').content.cloneNode(true));
        item.querySelector('[data-wishlist-toggle]')?.replaceWith(remove);
      }
      return item;
    }

    render() {
      const ids = this.ids().filter((id) => this.pool.has(String(id)));
      this.grid.replaceChildren(...ids.map((id) => this.card(id)).filter(Boolean));
      this.querySelector('[data-wishlist-count]').textContent = ids.length ? this.countText(ids.length) : '';
      this.querySelector('[data-wishlist-empty]').hidden = ids.length > 0;
      this.querySelector('[data-wishlist-actions]').hidden = ids.length === 0 || Boolean(this.sharedIds);
      this.querySelector('[data-wishlist-shared]').hidden = !this.sharedIds;
      this.grid.hidden = ids.length === 0;
      this.updateFitting(ids);
      this.renderRecommendations(ids);
      document.dispatchEvent(new CustomEvent('demo-store:change', { detail: { key: 'wishlist-render' } }));
    }

    updateFitting(ids) {
      const link = this.querySelector('[data-wishlist-fitting]');
      if (!link) return;
      const handles = ids.map((id) => this.pool.get(String(id))?.dataset.handle).filter(Boolean);
      link.href = `/pages/fitting?products=${encodeURIComponent(handles.join(','))}`;
    }

    /** Up to six similar products of the listed items, in list order, never one already listed. */
    renderRecommendations(ids) {
      const block = this.querySelector('[data-wishlist-recs]');
      if (!block) return;
      if (this.sharedIds || ids.length === 0) {
        block.hidden = true;
        return;
      }
      const listed = new Set(ids.map((id) => this.pool.get(String(id))?.dataset.handle));
      // Signed in, what she already has (wardrobe, orders) is not recommended to her (stage א4).
      if (window.DemoStore?.signedIn()) (window.AmorAccount?.seed()?.owned || []).forEach((handle) => handle && listed.add(handle));
      const byHandle = new Map([...this.pool.values()].map((t) => [t.dataset.handle, t]));
      const picked = [];
      ids.forEach((id) => {
        (this.pool.get(String(id))?.dataset.similar || '').split(',').forEach((handle) => {
          if (!handle || listed.has(handle) || picked.includes(handle) || !byHandle.has(handle)) return;
          picked.push(handle);
        });
      });
      const items = picked.slice(0, RECS_LIMIT).map((handle) => byHandle.get(handle).content.firstElementChild.cloneNode(true));
      this.querySelector('[data-wishlist-recs-list]').replaceChildren(...items);
      block.hidden = items.length === 0;
    }

    onClick(event) {
      const remove = event.target.closest('[data-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        this.remove(remove.closest('li'), remove.dataset.wishlistRemove);
        return;
      }
      const undo = event.target.closest('[data-wishlist-undo]');
      if (undo) {
        this.undo(undo.closest('li'));
        return;
      }
      if (event.target.closest('[data-wishlist-share]')) this.share();
    }

    /** The card gives way to "removed · undo" for five seconds. */
    remove(item, id) {
      if (!item) return;
      this.ownChange = true;
      window.DemoStore?.toggleWishlist(id);
      this.ownChange = false;
      const placeholder = document.createElement('li');
      placeholder.className = 'wishlist__removed';
      placeholder.setAttribute('role', 'status');
      placeholder.removedId = id;
      placeholder.removedCard = item;
      const text = document.createElement('span');
      text.textContent = `${this.dataset.removed} · `;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'link';
      button.dataset.wishlistUndo = '';
      button.textContent = this.dataset.labelUndo;
      placeholder.append(text, button);
      item.replaceWith(placeholder);
      this.updateCount();
      placeholder.timer = window.setTimeout(() => {
        placeholder.remove();
        if (!this.grid.children.length) this.render();
        else this.renderRecommendations(window.DemoStore?.wishlist() || []);
      }, UNDO_MS);
    }

    undo(placeholder) {
      if (!placeholder?.removedCard) return;
      window.clearTimeout(placeholder.timer);
      this.ownChange = true;
      if (!window.DemoStore?.inWishlist(placeholder.removedId)) window.DemoStore?.toggleWishlist(placeholder.removedId);
      this.ownChange = false;
      placeholder.replaceWith(placeholder.removedCard);
      this.updateCount();
    }

    updateCount() {
      const count = (window.DemoStore?.wishlist() || []).length;
      this.querySelector('[data-wishlist-count]').textContent = count ? this.countText(count) : '';
    }

    /** The phone's share sheet; on the desktop, the link is copied. */
    async share() {
      const ids = window.DemoStore?.wishlist() || [];
      const url = new URL(window.location.pathname, window.location.origin);
      url.search = `?items=${ids.join(',')}`;
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      if (coarse && navigator.share) {
        try {
          await navigator.share({ title: this.dataset.shareTitle, url: url.toString() });
        } catch {
          /* closed without sharing */
        }
        return;
      }
      const note = this.querySelector('[data-wishlist-copied]');
      try {
        await navigator.clipboard.writeText(url.toString());
        note.textContent = this.dataset.copied;
      } catch {
        // No clipboard access: show the link to copy by hand.
        note.textContent = url.toString();
      }
      note.hidden = false;
      window.clearTimeout(this.copiedTimer);
      this.copiedTimer = window.setTimeout(() => (note.hidden = true), UNDO_MS);
    }
  }
  customElements.define('amor-wishlist', AmorWishlist);
})();
